import React, { useState } from 'react';
import { Leaf } from 'lucide-react';
import { SEO } from '../components/SEO';

interface EcoFootprintState {
  transportation: {
    carKm: string;
    publicTransitKm: string;
    flightsPerYear: string;
  };
  housing: {
    householdSize: string;
    homeSize: string;
    energyEfficient: boolean;
  };
  lifestyle: {
    meatConsumption: 'high' | 'medium' | 'low' | 'none';
    localFood: boolean;
    recycling: boolean;
    shopping: 'high' | 'medium' | 'low';
  };
}

interface FootprintResult {
  carbonFootprint: number;
  earthsNeeded: number;
  breakdown: {
    transportation: number;
    housing: number;
    lifestyle: number;
  };
}

function KalkulatorEkoloskogOtiska() {
  const [values, setValues] = useState<EcoFootprintState>({
    transportation: {
      carKm: '',
      publicTransitKm: '',
      flightsPerYear: ''
    },
    housing: {
      householdSize: '',
      homeSize: '',
      energyEfficient: false
    },
    lifestyle: {
      meatConsumption: 'medium',
      localFood: false,
      recycling: false,
      shopping: 'medium'
    }
  });

  const [results, setResults] = useState<FootprintResult | null>(null);

  const calculateFootprint = () => {
    // Transportation impact
    const carImpact = (parseFloat(values.transportation.carKm) || 0) * 0.2; // 0.2 kg CO2 per km
    const transitImpact = (parseFloat(values.transportation.publicTransitKm) || 0) * 0.05; // 0.05 kg CO2 per km
    const flightImpact = (parseFloat(values.transportation.flightsPerYear) || 0) * 1000; // 1000 kg CO2 per flight
    const transportationTotal = carImpact + transitImpact + flightImpact;

    // Housing impact
    const householdSize = parseFloat(values.housing.householdSize) || 1;
    const homeSize = parseFloat(values.housing.homeSize) || 0;
    const energyFactor = values.housing.energyEfficient ? 0.8 : 1;
    const housingTotal = (homeSize * 20 * energyFactor) / householdSize; // 20 kg CO2 per m² annually

    // Lifestyle impact
    const meatFactors = {
      high: 1500,
      medium: 1000,
      low: 500,
      none: 100
    };
    const shoppingFactors = {
      high: 1000,
      medium: 500,
      low: 250
    };
    
    const lifestyleTotal = 
      meatFactors[values.lifestyle.meatConsumption] +
      shoppingFactors[values.lifestyle.shopping] -
      (values.lifestyle.localFood ? 200 : 0) -
      (values.lifestyle.recycling ? 300 : 0);

    const totalCarbonFootprint = transportationTotal + housingTotal + lifestyleTotal;
    const earthsNeeded = totalCarbonFootprint / 2000; // Average sustainable footprint is 2000 kg CO2/year

    setResults({
      carbonFootprint: parseFloat(totalCarbonFootprint.toFixed(2)),
      earthsNeeded: parseFloat(earthsNeeded.toFixed(2)),
      breakdown: {
        transportation: parseFloat(((transportationTotal / totalCarbonFootprint) * 100).toFixed(1)),
        housing: parseFloat(((housingTotal / totalCarbonFootprint) * 100).toFixed(1)),
        lifestyle: parseFloat(((lifestyleTotal / totalCarbonFootprint) * 100).toFixed(1))
      }
    });
  };

  const handleInputChange = (category: keyof EcoFootprintState, field: string, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const getFootprintCategory = (earthsNeeded: number): string => {
    if (earthsNeeded <= 1) return 'Održivo';
    if (earthsNeeded <= 2) return 'Umjereno';
    if (earthsNeeded <= 3) return 'Visoko';
    return 'Vrlo visoko';
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Ekološkog Otiska",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte svoj ekološki otisak i saznajte kako vaš životni stil utječe na okoliš. Uključuje transport, stanovanje i životne navike.",
    "url": "https://online-kalkulator.com/kalkulator-ekoloskog-otiska",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    }
  };

  return (
    <>
      <SEO 
        title="Kalkulator Ekološkog Otiska | Izračun Utjecaja na Okoliš"
        description="Izračunajte svoj ekološki otisak i saznajte kako vaš životni stil utječe na okoliš. Uključuje transport, stanovanje i životne navike."
        canonicalUrl="/kalkulator-ekoloskog-otiska"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Leaf className="w-8 h-8 text-green-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Ekološkog Otiska</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-8">
              {/* Transportation Section */}
              <section>
                <h2 className="text-xl font-semibold text-gray-700 mb-4">Transport</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Godišnja kilometraža automobilom
                    </label>
                    <input
                      type="number"
                      value={values.transportation.carKm}
                      onChange={(e) => handleInputChange('transportation', 'carKm', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="npr. 15000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Godišnja kilometraža javnim prijevozom
                    </label>
                    <input
                      type="number"
                      value={values.transportation.publicTransitKm}
                      onChange={(e) => handleInputChange('transportation', 'publicTransitKm', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="npr. 5000"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Broj letova godišnje
                    </label>
                    <input
                      type="number"
                      value={values.transportation.flightsPerYear}
                      onChange={(e) => handleInputChange('transportation', 'flightsPerYear', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="npr. 2"
                    />
                  </div>
                </div>
              </section>

              {/* Housing Section */}
              <section>
                <h2 className="text-xl font-semibold text-gray-700 mb-4">Stanovanje</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Broj članova kućanstva
                    </label>
                    <input
                      type="number"
                      value={values.housing.householdSize}
                      onChange={(e) => handleInputChange('housing', 'householdSize', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="npr. 3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Veličina stambenog prostora (m²)
                    </label>
                    <input
                      type="number"
                      value={values.housing.homeSize}
                      onChange={(e) => handleInputChange('housing', 'homeSize', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="npr. 80"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={values.housing.energyEfficient}
                        onChange={(e) => handleInputChange('housing', 'energyEfficient', e.target.checked)}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">
                        Koristim energetski učinkovite uređaje i LED rasvjetu
                      </span>
                    </label>
                  </div>
                </div>
              </section>

              {/* Lifestyle Section */}
              <section>
                <h2 className="text-xl font-semibold text-gray-700 mb-4">Životne Navike</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Konzumacija mesa
                    </label>
                    <select
                      value={values.lifestyle.meatConsumption}
                      onChange={(e) => handleInputChange('lifestyle', 'meatConsumption', e.target.value as 'high' | 'medium' | 'low' | 'none')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="high">Svakodnevno (visoka)</option>
                      <option value="medium">2-3 puta tjedno (srednja)</option>
                      <option value="low">Rijetko (niska)</option>
                      <option value="none">Nikad (vegetarijanac/vegan)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kupovina i potrošnja
                    </label>
                    <select
                      value={values.lifestyle.shopping}
                      onChange={(e) => handleInputChange('lifestyle', 'shopping', e.target.value as 'high' | 'medium' | 'low')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="high">Često kupujem nove stvari</option>
                      <option value="medium">Umjerena potrošnja</option>
                      <option value="low">Minimalna potrošnja</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={values.lifestyle.localFood}
                        onChange={(e) => handleInputChange('lifestyle', 'localFood', e.target.checked)}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">
                        Preferiram lokalnu i sezonsku hranu
                      </span>
                    </label>

                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={values.lifestyle.recycling}
                        onChange={(e) => handleInputChange('lifestyle', 'recycling', e.target.checked)}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">
                        Redovito recikliram
                      </span>
                    </label>
                  </div>
                </div>
              </section>

              {/* Calculate Button */}
              <button
                onClick={calculateFootprint}
                className="w-full py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                Izračunaj Ekološki Otisak
              </button>

              {/* Results */}
              {results && (
                <div className="space-y-6 border-t pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 mb-2">
                      {results.carbonFootprint} kg CO₂/godišnje
                    </div>
                    <div className="text-lg text-gray-700">
                      Potrebno je {results.earthsNeeded} Zemlje za održivost vašeg životnog stila
                    </div>
                    <div className="text-md text-gray-600 mt-1">
                      Kategorija: {getFootprintCategory(results.earthsNeeded)}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-700">Raspodjela utjecaja:</h3>
                    <div className="space-y-2">
                      <div>
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Transport</span>
                          <span>{results.breakdown.transportation}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500"
                            style={{ width: `${results.breakdown.transportation}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Stanovanje</span>
                          <span>{results.breakdown.housing}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-yellow-500"
                            style={{ width: `${results.breakdown.housing}%` }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Životne navike</span>
                          <span>{results.breakdown.lifestyle}%</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500"
                            style={{ width: `${results.breakdown.lifestyle}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što je Ekološki Otisak?</h2>
                <p className="text-gray-600 leading-relaxed">
                  Ekološki otisak je mjera ljudskog utjecaja na Zemljine ekosustave. Izračunava se kroz
                  analizu potrošnje prirodnih resursa i proizvodnje otpada u odnosu na kapacitet Zemlje
                  da regenerira resurse i apsorbira otpad.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako se Računa</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Transport: Uključuje emisije CO₂ od različitih oblika prijevoza</li>
                  <li>Stanovanje: Računa potrošnju energije i resursa u kućanstvu</li>
                  <li>Životne navike: Procjenjuje utjecaj prehrambenih i potrošačkih navika</li>
                  <li>Rezultat se izražava u:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Kilogramima CO₂ godišnje</li>
                      <li>- Broju potrebnih planeta za održivost</li>
                      <li>- Postotnoj raspodjeli utjecaja</li>
                    </ul>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kategorije Utjecaja</h2>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="font-medium">Održivo</span>
                    <span>≤ 1 Zemlja</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span className="font-medium">Umjereno</span>
                    <span>1-2 Zemlje</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                    <span className="font-medium">Visoko</span>
                    <span>2-3 Zemlje</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="font-medium">Vrlo visoko</span>
                    <span>3+ Zemlje</span>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Smanjiti Ekološki Otisak</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Transport</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Koristite javni prijevoz</li>
                      <li>Vozite bicikl ili hodajte</li>
                      <li>Dijelite vožnju s drugima</li>
                      <li>Razmotrite električno vozilo</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Stanovanje</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Koristite LED rasvjetu</li>
                      <li>Poboljšajte izolaciju</li>
                      <li>Instalirajte solarne panele</li>
                      <li>Štedite vodu i energiju</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Prehrana</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Smanjite konzumaciju mesa</li>
                      <li>Kupujte lokalnu hranu</li>
                      <li>Izbjegavajte bacanje hrane</li>
                      <li>Uzgajajte vlastito povrće</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Potrošnja</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Reciklirajte i kompostirajte</li>
                      <li>Kupujte rabljene stvari</li>
                      <li>Izbjegavajte jednokratnu plastiku</li>
                      <li>Popravljajte umjesto zamjene</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorEkoloskogOtiska;